import { FC, useMemo } from 'react';
import { Interpolation, Theme, css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { Trans, useTranslation } from '@frontend/i18n';
import { breakpoints, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, InlineNotification, Text, TextLink } from '@frontend/design-system';
import { PRIVATE_COMMS_HELP_LINK } from '../../constants';
import { usePrivateCommsBannerVisibility } from '../../hooks/use-private-comms-banner-visibility';
import { getPrivateCommsBannerTrackingId } from '../../tracking-ids';
import { CallIntelPrivateCommsBannerSource } from '../../types';

interface PrivateCommsBannerProps {
  source: CallIntelPrivateCommsBannerSource;
  customStyles?: Interpolation<Theme>;
  innerRef?: React.Ref<HTMLDivElement>;
  onDismiss?: () => void;
}

export const PrivateCommsBanner: FC<PrivateCommsBannerProps> = ({ source, innerRef, customStyles, onDismiss }) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'call-intel' });

  const { ref, handleDismiss, shouldShowBanner } = usePrivateCommsBannerVisibility({
    source,
    enabled: true,
    onDismiss,
  });

  const mainContentElement = useMemo(() => document.querySelector('#main-content') as HTMLElement, []);

  const [isSmallMode] = useTargetElementContainerQuery({
    maxWidth: breakpoints.xsmall.max,
    element: mainContentElement,
  });

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <InlineNotification
      status='info'
      ref={composeRefs(innerRef, ref) as React.Ref<HTMLDivElement>}
      size={isSmallMode ? 'small' : 'medium'}
      customStyles={[styles.wrapper, customStyles]}
    >
      <Text css={styles.text}>
        <Trans t={t}>
          Some analyzed calls are private and hidden from this table.{' '}
          <TextLink
            target='_blank'
            rel='noopener noreferrer'
            href={PRIVATE_COMMS_HELP_LINK}
            trackingId={getPrivateCommsBannerTrackingId({
              source,
              action: 'learn-more',
            })}
            css={styles.linkBtn}
          >
            Learn More
          </TextLink>
        </Trans>
      </Text>
      <Button
        variant='secondary'
        onClick={handleDismiss}
        trackingId={getPrivateCommsBannerTrackingId({
          source,
          action: 'dismiss',
        })}
        iconName='x-small'
        aria-label={t('Dismiss')}
        css={styles.closeBtn}
      />
    </InlineNotification>
  );
};

const styles = {
  wrapper: css`
    max-height: none;
  `,
  linkBtn: css`
    white-space: nowrap;
  `,
  closeBtn: css`
    padding: ${theme.spacing(0)};
    height: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.max}px) {
      height: ${theme.spacing(1.5)};
      span {
        min-width: ${theme.spacing(1.5)};
      }
      svg {
        height: ${theme.spacing(1.5)};
        width: ${theme.spacing(1.5)};
      }
    }
  `,
  text: css`
    font-size: ${theme.font.size.medium};
    @container (max-width: ${breakpoints.small.max}px) {
      font-size: ${theme.font.size.small};
    }
  `,
};
